import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DataTable,
  Badge,
  Button,
  Pagination,
  Text,
  InlineStack,
  Thumbnail,
  Icon,
} from '@shopify/polaris';
import { ExternalIcon } from '@shopify/polaris-icons';
import { formatRelativeOrDate } from '../utils/dateFormat';
import { GeminiIcon } from './PlatformIcon';

/**
 * UcpProductTable
 * Dedicated product table for the UCP/Gemini score filter view.
 * Shows UCP Score and Gemini Tier instead of overall score/tier.
 */

export type UcpSortKey = 'title_asc' | 'title_desc' | 'score_asc' | 'score_desc' | 'updated_asc' | 'updated_desc';

// Column index → [asc sort key, desc sort key]
// Columns: 0=Product, 1=UCP Score, 2=Gemini Tier, 3=GTIN, 4=Images, 5=Last Scored, 6=Store
const UCP_COLUMN_SORT_KEYS: Record<number, [UcpSortKey, UcpSortKey]> = {
  0: ['title_asc', 'title_desc'],
  1: ['score_asc', 'score_desc'],
  2: ['score_asc', 'score_desc'],
  5: ['updated_asc', 'updated_desc'],
};

export interface UcpProductItem {
  id: string;
  title: string;
  ucpScore: number | null;
  barcodeStatus: string;
  imageCount: number;
  featuredImageUrl: string | null;
  lastScoredAt: string | null;
  storefrontUrl: string | null;
}

interface Props {
  products: UcpProductItem[];
  productIds: string[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  sortBy: UcpSortKey;
  onSort: (sortBy: UcpSortKey) => void;
  onPageChange: (page: number) => void;
  emptyText: string;
}

function getUcpTierBadge(score: number | null): { label: string; tone: 'success' | 'attention' | 'critical' | 'info' } {
  if (score === null) return { label: 'Not scored', tone: 'info' };
  if (score >= 80) return { label: 'Discoverable', tone: 'success' };
  if (score >= 60) return { label: 'Readable', tone: 'success' };
  if (score >= 40) return { label: 'Confusing', tone: 'attention' };
  return { label: 'Invisible', tone: 'critical' };
}

function barcodeStatusBadge(status: string): { label: string; tone: 'success' | 'warning' | 'critical' } {
  switch (status) {
    case 'VALID': return { label: 'Valid', tone: 'success' };
    case 'INVALID': return { label: 'Invalid', tone: 'critical' };
    default: return { label: 'Missing', tone: 'warning' };
  }
}

export function UcpProductTable({
  products,
  productIds,
  total,
  page,
  pageSize,
  totalPages,
  sortBy,
  onSort,
  onPageChange,
  emptyText,
}: Props): React.ReactElement {
  const navigate = useNavigate();

  const sortDirection = sortBy.endsWith('_asc') ? 'ascending' : 'descending';
  const sortColumnIndex = Object.entries(UCP_COLUMN_SORT_KEYS).find(
    ([, [asc, desc]]) => asc === sortBy || desc === sortBy,
  )?.[0];
  const initialSortColumnIndex = sortColumnIndex === undefined ? undefined : Number(sortColumnIndex);

  const handleSort = useCallback(
    (headingIndex: number, direction: 'ascending' | 'descending') => {
      const keys = UCP_COLUMN_SORT_KEYS[headingIndex];
      if (!keys) return;
      onSort(direction === 'ascending' ? keys[0] : keys[1]);
    },
    [onSort],
  );

  const rows = products.map((product, index) => {
    const tier = getUcpTierBadge(product.ucpScore);
    const barcode = barcodeStatusBadge(product.barcodeStatus);
    return [
      <div key={`ucp-product-${product.id}`} className="prodlist-product-cell">
        <div className="prodlist-product-thumb-wrap">
          <Thumbnail
            source={product.featuredImageUrl ?? 'placeholder'}
            alt=""
            size="extraSmall"
          />
        </div>
        <Button
          variant="plain"
          onClick={() => navigate(`/products/${product.id}`, { state: { productIds, currentIndex: index } })}
        >
          {product.title}
        </Button>
      </div>,
      product.ucpScore ?? '—',
      <Badge key={`ucp-tier-${product.id}`} tone={tier.tone}>
        {tier.label}
      </Badge>,
      <Badge key={`ucp-barcode-${product.id}`} tone={barcode.tone}>
        {barcode.label}
      </Badge>,
      product.imageCount,
      product.lastScoredAt ? formatRelativeOrDate(product.lastScoredAt) : '—',
      product.storefrontUrl ? (
        <a
          key={`ucp-store-${product.id}`}
          href={product.storefrontUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="View on Store"
        >
          <Icon source={ExternalIcon} tone="interactive" />
        </a>
      ) : '—',
    ];
  });

  const startItem = (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <>
      <div className="prodlist-table-wrapper">
        <DataTable
          columnContentTypes={['text', 'numeric', 'text', 'text', 'numeric', 'text', 'text']}
          headings={[
            'Product',
            <span key="score" className="prodlist-platform-heading"><GeminiIcon /> Score</span>,
            <span key="tier" className="prodlist-platform-heading"><GeminiIcon /> Tier</span>,
            'GTIN',
            'Images',
            'Last Scored',
            'Store',
          ]}
          rows={rows}
          sortable={[true, true, true, false, false, true, false]}
          defaultSortDirection={sortDirection}
          initialSortColumnIndex={initialSortColumnIndex}
          onSort={handleSort}
        />
      </div>

      {total > 0 && (
        <div className="prodlist-pagination-row">
          <InlineStack align="center" blockAlign="center" gap="400">
            <Text as="span" variant="bodySm" tone="subdued">
              {`${startItem}–${endItem} of ${total}`}
            </Text>
            <Pagination
              hasPrevious={page > 1}
              onPrevious={() => onPageChange(Math.max(1, page - 1))}
              hasNext={page < totalPages}
              onNext={() => onPageChange(Math.min(totalPages, page + 1))}
              label={`Page ${page} of ${totalPages}`}
            />
          </InlineStack>
        </div>
      )}

      {total === 0 && (
        <div className="prodlist-empty-state">
          <Text as="p" variant="bodyMd" tone="subdued">{emptyText}</Text>
        </div>
      )}
    </>
  );
}
